import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Clock, Activity } from 'lucide-react';

interface PlayByPlayLoggerProps {
  events: any[];
}

const PlayByPlayLogger = ({ events }: PlayByPlayLoggerProps) => {
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const getEventVariant = (eventType: string) => {
    switch (eventType) {
      case 'score':
        return 'default';
      case 'foul':
      case 'penalty':
      case 'red_card':
        return 'destructive';
      case 'timeout':
      case 'substitution':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatEventDescription = (event: any) => {
    switch (event.type) {
      case 'score':
        return `${event.team === 'home' ? 'Home' : 'Away'} team scores ${event.points} point(s). New score: ${event.newScore}`;
      default:
        return event.label || event.type;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Activity className="h-5 w-5" />
          Play by Play Log
          <Badge variant="outline">{events.length} Events</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No events recorded yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Game events will appear here as they happen
            </p>
          </div>
        ) : (
          <ScrollArea className="h-96">
            <div className="space-y-3">
              {events.slice().reverse().map((event, index) => (
                <div
                  key={event.id || index}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card/50"
                >
                  <div className="text-xs text-muted-foreground min-w-fit">
                    {formatTimestamp(event.timestamp)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={getEventVariant(event.type)} className="text-xs">
                        {event.type.replace('_', ' ').toUpperCase()}
                      </Badge>
                      {event.period && (
                        <Badge variant="outline" className="text-xs">
                          Period {event.period}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-foreground">
                      {formatEventDescription(event)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default PlayByPlayLogger;